export type RolePermissionKey =
  | 'manage_users'
  | 'manage_roles'
  | 'manage_checks'
  | 'manage_templates'
  | 'manage_locations'
  | 'view_reports'
  | 'manage_settings'
  | 'assign_job_tags';

export type RolePermissions = Record<RolePermissionKey, boolean>;

export const ROLE_PERMISSION_KEYS: RolePermissionKey[] = [
  'manage_users',
  'manage_roles',
  'manage_checks',
  'manage_templates',
  'manage_locations',
  'view_reports',
  'manage_settings',
  'assign_job_tags',
];

export const ROLE_PERMISSION_LABELS: Record<RolePermissionKey, string> = {
  manage_users: 'Manage users',
  manage_roles: 'Manage roles',
  manage_checks: 'Manage checks',
  manage_templates: 'Manage templates',
  manage_locations: 'Manage locations',
  view_reports: 'View reports',
  manage_settings: 'Manage settings',
  assign_job_tags: 'Assign job tags',
};

export const ROLE_PERMISSION_DESCRIPTIONS: Record<RolePermissionKey, string> = {
  manage_users: 'Create, edit, and deactivate users',
  manage_roles: 'Configure role permissions for this business',
  manage_checks: 'Create and manage safety checks',
  manage_templates: 'Create and edit check templates',
  manage_locations: 'Manage locations and related settings',
  view_reports: 'View reports and response history',
  manage_settings: 'Access business settings',
  assign_job_tags: 'Assign and manage job tags',
};

type TokenPayload = {
  role?: string;
  permissions?: Partial<RolePermissions>;
  multiBusinessManager?: boolean;
  impersonating?: boolean;
};

const decodeTokenPayload = (): TokenPayload | null => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1])) as TokenPayload;
  } catch {
    return null;
  }
};

export const getPermissionsFromToken = (): Partial<RolePermissions> => {
  const payload = decodeTokenPayload();
  if (!payload?.permissions || typeof payload.permissions !== 'object') return {};
  return payload.permissions;
};

export const getRoleFromToken = (): string | null => {
  return decodeTokenPayload()?.role || null;
};

/** True when the current user may configure business role permissions. */
export const canManageRolesFromToken = (): boolean => {
  const payload = decodeTokenPayload();
  if (!payload) return false;
  if (
    payload.role === 'Admin' ||
    payload.role === 'Super Admin' ||
    payload.role === 'Owner' ||
    payload.role === 'Multi Business Manager'
  ) {
    return true;
  }
  if (payload.multiBusinessManager === true) return true;
  if (localStorage.getItem('inBusinessContext') === 'true') return true;
  if (localStorage.getItem('impersonatingBusiness') === 'true') return true;
  return payload.permissions?.manage_roles === true;
};

export const hasPermissionFromToken = (key: RolePermissionKey): boolean => {
  const payload = decodeTokenPayload();
  if (!payload) return false;
  if (
    payload.role === 'Super Admin' ||
    payload.role === 'Owner' ||
    payload.role === 'Multi Business Manager'
  ) {
    return true;
  }
  if (payload.multiBusinessManager === true) return true;
  if (localStorage.getItem('inBusinessContext') === 'true') return true;
  if (localStorage.getItem('impersonatingBusiness') === 'true') return true;
  if (payload.permissions?.[key] === true) return true;
  // Admins historically had full access; treat missing key as allowed for Admin only
  if (payload.role === 'Admin' && payload.permissions?.[key] !== false) return true;
  return false;
};
